import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

interface UseDataGridSearchArgs {
  /** The initial search string, typically from URL params via `useDataGridUrlState`. */
  initialSearch: string;
  /** Whether the DataGrid is using server-side data fetching. Affects debounce and sync behavior. */
  isServerSide: boolean;
  /** Server-side callback fired when the search value commits (after debounce or Enter key). */
  onSearchChange?: (search: string) => void;
  /** Resets pagination to page 1. Called on client-side search changes to avoid showing an empty page. */
  resetPagination: () => void;
}

interface UseDataGridSearchReturn {
  /** The committed search value used for filtering (passed to TanStack's `globalFilter`). */
  search: string;
  /** The live input value shown in the search field. May differ from `search` during server-side debounce. */
  searchInput: string;
  /** Ref to the search input element for focus management. */
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  /** Input change handler. Client-side: applies immediately. Server-side: debounced (500ms). */
  handleSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Keydown handler. Enter key bypasses debounce and commits the search immediately. */
  handleSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Clears both the input and committed search value. */
  handleClearSearch: () => void;
  /** Direct setter for the committed search value. */
  setSearch: Dispatch<SetStateAction<string>>;
}

/**
 * Manages DataGrid search state with different behavior for client-side and server-side modes.
 *
 * - **Client-side**: Search applies immediately on each keystroke. Resets pagination to page 1.
 * - **Server-side**: Search input is debounced (500ms) before committing. Enter key bypasses the
 *   debounce. External state changes (e.g., browser back/forward) sync via `initialSearch`.
 *
 * Maintains two pieces of state: `searchInput` (the live input value) and `search` (the committed
 * value used for filtering). During server-side debounce, these may temporarily differ.
 */
export function useDataGridSearch({
  initialSearch,
  isServerSide,
  onSearchChange,
  resetPagination,
}: UseDataGridSearchArgs): UseDataGridSearchReturn {
  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);

      if (isServerSide) {
        onSearchChange?.(value);
      } else {
        // For client-side, reset to first page when search changes
        resetPagination();
      }
    },
    [isServerSide, onSearchChange, resetPagination],
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      const value = e.target.value;

      setSearchInput(value);

      // For client-side, apply search immediately
      if (!isServerSide) {
        handleSearchChange(value);
        return;
      }

      // For server-side, debounce the search
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        handleSearchChange(value);
      }, 500);
    },
    [isServerSide, handleSearchChange],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        // Clear any pending debounced search
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        handleSearchChange(searchInput);
      }
    },
    [searchInput, handleSearchChange],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    handleSearchChange('');
  }, [handleSearchChange]);

  // Sync from external state changes (e.g., browser back/forward navigation updating URL params)
  // in server-side mode. Without this, the search input would show stale text after navigation.
  // Clears any pending debounce timer to prevent a stale in-flight search from overwriting
  // the externally synced value.
  useEffect(() => {
    if (isServerSide) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setSearch(initialSearch);
      setSearchInput(initialSearch);
    }
  }, [isServerSide, initialSearch]);

  // Sync search input with search for display purposes
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    search,
    searchInput,
    searchInputRef,
    handleSearchInputChange,
    handleSearchKeyDown,
    handleClearSearch,
    setSearch,
  };
}
